import type { SeriesCreateRequest } from '../../api/types';
import { GameMode, ServerRegion } from '../../domain/enums';
import {
  DEFAULT_SERIES_FORMAT,
  seriesComplete,
  seriesFormatInfo,
  type SeriesFormat,
} from '../../domain/series';
import type { ConfigData, Team } from '../../store/config';

export const MAX_PLAYERS_PER_TEAM = 5;

export type SeriesVisibility = 'public' | 'friends' | 'unlisted';

/**
 * Timeouts are kept as strings so that "blank" can mean "server default": the
 * API's defaults are generous and most series never need to touch them.
 */
export type SeriesFormValues = {
  radiantCaptain?: string;
  direCaptain?: string;
  radiantStarters?: string[];
  direStarters?: string[];
  rosterPolicy?: 'open' | 'organizer_approval' | 'locked_lineup';
  game_name: string;
  format: SeriesFormat;
  radiantTeamId: string;
  direTeamId: string;
  players_per_team: number;
  password: string;
  server_region: number;
  game_mode: number;
  league_id: number;
  visibility: SeriesVisibility;
  allchat: boolean;
  teams_fill_timeout_minutes: string;
  between_games_timeout_minutes: string;
  decision_timeout_minutes: string;
  game_duration_timeout_minutes: string;
  webhook_url: string;
  webhook_secret: string;
  /** 1 for a new series; higher to resume an interrupted one. */
  game_number: number;
  initial_radiant_wins: number;
  initial_dire_wins: number;
};

/** The API's own defaults, shown as placeholders so a blank field is explained. */
export const SERIES_TIMEOUT_DEFAULT_MINUTES = {
  teams_fill: 30,
  between_games: 10,
  decision: 15,
  game_duration: 180,
} as const;

export function initialSeriesFormValues(config: ConfigData): SeriesFormValues {
  return {
    game_name: config.lobbyNameDefault,
    format: DEFAULT_SERIES_FORMAT,
    rosterPolicy: 'organizer_approval',
    radiantTeamId: '',
    direTeamId: '',
    players_per_team: MAX_PLAYERS_PER_TEAM,
    password: '',
    server_region: config.defaultServerRegion || ServerRegion.EuropeWest,
    game_mode: config.defaultGameMode || GameMode.CaptainsMode,
    league_id: config.defaultLeagueId || 0,
    visibility: 'public',
    allchat: false,
    teams_fill_timeout_minutes: '',
    between_games_timeout_minutes: '',
    decision_timeout_minutes: '',
    game_duration_timeout_minutes: '',
    webhook_url: '',
    webhook_secret: '',
    game_number: 1,
    initial_radiant_wins: 0,
    initial_dire_wins: 0,
  };
}

export type SeriesFormError = {
  field: keyof SeriesFormValues;
  message: string;
};

function findTeam(teams: Team[], teamId: string): Team | undefined {
  if (!teamId) return undefined;
  return teams.find((team) => team.id === teamId);
}

function validateTeam(
  field: 'radiantTeamId' | 'direTeamId',
  sideLabel: string,
  teams: Team[],
  teamId: string,
  playersPerTeam: number,
  errors: SeriesFormError[],
): void {
  if (!teamId) {
    errors.push({ field, message: `Pick the ${sideLabel} team` });
    return;
  }
  const team = findTeam(teams, teamId);
  if (!team) {
    errors.push({ field, message: `The ${sideLabel} team no longer exists` });
    return;
  }
  const teamName = team.name || 'This team';
  if (team.playerSteamIds.length === 0) {
    errors.push({ field, message: `${teamName} has no players` });
    return;
  }
  if (team.playerSteamIds.length < playersPerTeam) {
    errors.push({
      field,
      message: `${teamName} has ${team.playerSteamIds.length} player(s); ${playersPerTeam} per team are needed. Add players to the team or lower players per team.`,
    });
  }
}

function validateMinutes(
  field: keyof SeriesFormValues,
  raw: string,
  errors: SeriesFormError[],
): void {
  if (raw.trim() === '') return;
  const minutes = Number(raw);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    errors.push({
      field,
      message:
        'Leave empty for the default, or enter a number of minutes above 0',
    });
  }
}

/**
 * Everything the API would reject, checked before the request leaves, plus
 * the cases where the series would start but could never fill.
 */
export function validateSeriesForm(
  values: SeriesFormValues,
  teams: Team[],
): SeriesFormError[] {
  const errors: SeriesFormError[] = [];
  const info = seriesFormatInfo(values.format);
  const playersPerTeam = Number(values.players_per_team);

  if (!values.game_name.trim()) {
    errors.push({ field: 'game_name', message: 'A series name is required' });
  }

  if (
    !Number.isInteger(playersPerTeam) ||
    playersPerTeam < 1 ||
    playersPerTeam > MAX_PLAYERS_PER_TEAM
  ) {
    errors.push({
      field: 'players_per_team',
      message: `Between 1 and ${MAX_PLAYERS_PER_TEAM} players per team`,
    });
  }

  for (const [field, selected] of [
    ['radiantStarters', values.radiantStarters],
    ['direStarters', values.direStarters],
  ] as const) {
    if (selected && selected.length !== playersPerTeam)
      errors.push({
        field,
        message: `Select exactly ${playersPerTeam} starters`,
      });
  }
  const radiantPlayers =
    values.radiantStarters ??
    findTeam(teams, values.radiantTeamId)?.playerSteamIds ??
    [];
  const direPlayers =
    values.direStarters ??
    findTeam(teams, values.direTeamId)?.playerSteamIds ??
    [];
  if (
    new Set([...radiantPlayers, ...direPlayers]).size !==
    radiantPlayers.length + direPlayers.length
  )
    errors.push({
      field: 'direTeamId',
      message: 'Lineups contain duplicate or overlapping players',
    });
  validateTeam(
    'radiantTeamId',
    'Radiant',
    teams,
    values.radiantTeamId,
    playersPerTeam,
    errors,
  );
  validateTeam(
    'direTeamId',
    'Dire',
    teams,
    values.direTeamId,
    playersPerTeam,
    errors,
  );
  if (values.radiantTeamId && values.radiantTeamId === values.direTeamId) {
    errors.push({
      field: 'direTeamId',
      message: 'Radiant and Dire must be different teams',
    });
  }

  if (Number(values.league_id) < 0) {
    errors.push({
      field: 'league_id',
      message: 'League ID cannot be negative',
    });
  }

  validateMinutes(
    'teams_fill_timeout_minutes',
    values.teams_fill_timeout_minutes,
    errors,
  );
  validateMinutes(
    'between_games_timeout_minutes',
    values.between_games_timeout_minutes,
    errors,
  );
  validateMinutes(
    'decision_timeout_minutes',
    values.decision_timeout_minutes,
    errors,
  );
  validateMinutes(
    'game_duration_timeout_minutes',
    values.game_duration_timeout_minutes,
    errors,
  );

  const webhook = values.webhook_url.trim();
  if (webhook && !/^https?:\/\//i.test(webhook)) {
    errors.push({
      field: 'webhook_url',
      message: 'The webhook URL must start with http:// or https://',
    });
  }

  const gameNumber = Number(values.game_number);
  const radiantWins = Number(values.initial_radiant_wins);
  const direWins = Number(values.initial_dire_wins);
  if (
    !Number.isInteger(gameNumber) ||
    gameNumber < 1 ||
    gameNumber > info.maxGames
  ) {
    errors.push({
      field: 'game_number',
      message: `A ${info.label.toLowerCase()} starts at game 1 to ${info.maxGames}`,
    });
  } else {
    const played = gameNumber - 1;
    if (radiantWins < 0 || direWins < 0) {
      errors.push({
        field: 'initial_radiant_wins',
        message: 'Wins cannot be negative',
      });
    } else if (radiantWins + direWins > played) {
      errors.push({
        field: 'initial_radiant_wins',
        message: `${radiantWins}-${direWins} is more than the ${played} game(s) played before game ${gameNumber}`,
      });
    } else if (
      played > 0 &&
      seriesComplete(values.format, radiantWins, direWins, played)
    ) {
      errors.push({
        field: 'initial_radiant_wins',
        message: `A ${info.label.toLowerCase()} is already over at ${radiantWins}-${direWins}`,
      });
    }
  }

  return errors;
}

function minutesToSeconds(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const minutes = Number(raw);
  if (!Number.isFinite(minutes) || minutes <= 0) return undefined;
  return Math.round(minutes * 60);
}

function namesFor(
  config: ConfigData,
  steamIds: string[],
): Record<string, string> {
  const names: Record<string, string> = {};
  for (const steamId of steamIds) {
    const name = config.players
      .find((player) => player.steamId === steamId)
      ?.name?.trim();
    if (name) names[steamId] = name;
  }
  return names;
}

/**
 * Builds the API payload. Optional fields are omitted rather than sent empty
 * so the server keeps its own defaults; the resume fields only travel when
 * the series does not start at game 1.
 */
export function buildSeriesRequest(
  values: SeriesFormValues,
  config: ConfigData,
): SeriesCreateRequest {
  const radiant = [
    ...(values.radiantStarters ??
      findTeam(config.teams, values.radiantTeamId)?.playerSteamIds ??
      []),
  ];
  const dire = [
    ...(values.direStarters ??
      findTeam(config.teams, values.direTeamId)?.playerSteamIds ??
      []),
  ];

  const payload: SeriesCreateRequest = {
    team_a_name: findTeam(config.teams, values.radiantTeamId)?.name,
    team_b_name: findTeam(config.teams, values.direTeamId)?.name,
    game_name: values.game_name.trim(),
    roster_policy: values.rosterPolicy,
    format: values.format,
    players_by_team: { radiant, dire },
    players_per_team: Number(values.players_per_team),
    server_region: Number(values.server_region),
    game_mode: Number(values.game_mode),
    league_id: Number(values.league_id) || 0,
    visibility: values.visibility,
    allchat: values.allchat,
  };

  if (values.radiantCaptain && radiant.includes(values.radiantCaptain))
    payload.radiant_captain = values.radiantCaptain;
  if (values.direCaptain && dire.includes(values.direCaptain))
    payload.dire_captain = values.direCaptain;
  const names = namesFor(config, [...radiant, ...dire]);
  if (Object.keys(names).length > 0) payload.player_names = names;

  const password = values.password.trim();
  if (password) payload.password = password;

  const teamsFill = minutesToSeconds(values.teams_fill_timeout_minutes);
  if (teamsFill !== undefined) payload.teams_fill_timeout_seconds = teamsFill;
  const betweenGames = minutesToSeconds(values.between_games_timeout_minutes);
  if (betweenGames !== undefined)
    payload.between_games_timeout_seconds = betweenGames;
  const decision = minutesToSeconds(values.decision_timeout_minutes);
  if (decision !== undefined) payload.decision_timeout_seconds = decision;
  const gameDuration = minutesToSeconds(values.game_duration_timeout_minutes);
  if (gameDuration !== undefined)
    payload.game_duration_timeout_seconds = gameDuration;

  const webhook = values.webhook_url.trim();
  if (webhook) {
    payload.webhook_url = webhook;
    const secret = values.webhook_secret.trim();
    if (secret) payload.webhook_secret = secret;
  }

  const gameNumber = Number(values.game_number);
  if (gameNumber > 1) {
    payload.game_number = gameNumber;
    payload.initial_radiant_wins = Number(values.initial_radiant_wins) || 0;
    payload.initial_dire_wins = Number(values.initial_dire_wins) || 0;
  }

  return payload;
}
